import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../../redux/rootReducer";
import { AppThunk } from "../../../../redux/store";
import BillsService from "../services/billsService";
import { IBill, IBillResponse } from "../../../../types/IBill";
import { IDocument } from "../../../../types/IDocument";
import { IProposal } from "../../../../types/IProposal";
import makeQueryString from "../../../../helpers/urlBuilder.helpers";
import { IBillProposal } from "../../../../types/IBillProposal";
import { IBillSearchInterest } from "../../../../types/IBillSearchInterest";
import { handleError } from "../../../../utils/helpers/helpers";

export interface BillsError {
  message: string;
  statusCode?: number;
}

export interface BillsState {
  bills: IBill[];
  fetchingBillsInProgress: boolean;
  fetchingBillsError: BillsError;
  currentBill: IBill;
  fetchingCurrentBillsInProgress: boolean;
  fetchingCurrentBillError: BillsError;
  currentBillLikeCount: number;
  currentBillDislikeCount: number;
  currentBillLikeUsers: string[];
  currentBillDislikeUsers: string[];
  fetchingCurrentBillVoteCountInProgress: boolean;
  fetchingCurrentBillVoteCountError: BillsError;
  currentDocument: IDocument;
  fetchingCurrentDocumentInProgress: boolean;
  fetchingCurrentDocumentError: BillsError;
  savingDocumentStateInProgress: boolean;
  savingDocumentStateError: BillsError;
  savingProposalStateInProgress: boolean;
  savingProposalStateError: BillsError;
  savingVoteBillStateInProgress: boolean;
  savingVoteBillStateError: BillsError;
  publishingProposalInProgress: boolean;
  publishingProposalError: BillsError;
  deletingProposalInProgress: boolean;
  deletingProposalError: BillsError;
  proposals: IProposal[];
  fetchingProposalsInProgress: boolean;
  fetchingProposalsError: BillsError;
  billSearchInterests: IBillSearchInterest[];
  fetchingBillSearchInterestsInProgress: boolean;
  fetchingBillSearchInterestsError: BillsError;
  myBillsSelected: boolean;
  billsOfInterestsSelected: boolean;
  allBillsSelected: boolean;
  billProposals: IBillProposal;
  fetchingBillProposalsInProgress: boolean;
  fetchingBillProposalsError: BillsError;
  offset: number;
  perPage: number;
  currentPage: number;
  pageCount: number | null;
  tag: string;
}

export const initialState: BillsState = {
  bills: [],
  fetchingBillsInProgress: false,
  fetchingBillsError: null,
  currentBill: null,
  fetchingCurrentBillsInProgress: false,
  fetchingCurrentBillError: null,
  currentBillLikeCount: 0,
  currentBillDislikeCount: 0,
  currentBillLikeUsers: [],
  currentBillDislikeUsers: [],
  fetchingCurrentBillVoteCountInProgress: false,
  fetchingCurrentBillVoteCountError: null,
  currentDocument: null,
  fetchingCurrentDocumentInProgress: false,
  fetchingCurrentDocumentError: null,
  savingDocumentStateInProgress: false,
  savingDocumentStateError: null,
  savingProposalStateInProgress: false,
  savingProposalStateError: null,
  savingVoteBillStateInProgress: false,
  savingVoteBillStateError: null,
  publishingProposalInProgress: false,
  publishingProposalError: null,
  deletingProposalInProgress: false,
  deletingProposalError: null,
  proposals: [],
  fetchingProposalsInProgress: false,
  fetchingProposalsError: null,
  billSearchInterests: [],
  fetchingBillSearchInterestsInProgress: false,
  fetchingBillSearchInterestsError: null,
  myBillsSelected: false,
  billsOfInterestsSelected: false,
  allBillsSelected: false,
  billProposals: null,
  fetchingBillProposalsInProgress: false,
  fetchingBillProposalsError: null,
  offset: 0,
  perPage: 50,
  currentPage: 0,
  pageCount: 0,
  tag: "",
};

export const billsSlice = createSlice({
  name: "bills",
  initialState,
  reducers: {
    setBillsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.fetchingBillsInProgress = payload;
    },
    setBillsSuccess: (state, { payload }: PayloadAction<IBillResponse>) => {
      state.bills = payload.bills;
      state.fetchingBillsInProgress = false;
      state.pageCount = Math.ceil(payload.count / state.perPage);
      state.fetchingBillsError = null;
    },
    setBillsFailed: (state, { payload }: PayloadAction<BillsError>) => {
      state.fetchingBillsError = payload;
    },
    setCurrentBillLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.fetchingCurrentBillsInProgress = payload;
    },
    setCurrentBillSuccess: (state, { payload }: PayloadAction<IBill>) => {
      state.currentBill = payload;
      state.fetchingCurrentBillError = null;
    },
    setCurrentBillsFailed: (state, { payload }: PayloadAction<BillsError>) => {
      state.fetchingCurrentBillError = payload;
    },
    setCurrentBillVoteCountLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.fetchingCurrentBillVoteCountInProgress = payload;
    },
    setCurrentBillVoteCountSuccess: (
      state,
      { payload }: PayloadAction<any>
    ) => {
      state.currentBillLikeCount = payload.likeCount;
      state.currentBillDislikeCount = payload.dislikeCount;
      state.currentBillLikeUsers = payload.likeUsers;
      state.currentBillDislikeUsers = payload.dislikeUsers;
      state.fetchingCurrentBillVoteCountError = null;
    },
    setCurrentBillVoteCountFailed: (
      state,
      { payload }: PayloadAction<BillsError>
    ) => {
      state.fetchingCurrentBillError = payload;
    },
    setDocumentLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.fetchingCurrentDocumentInProgress = payload;
    },
    setDocumentSuccess: (state, { payload }: PayloadAction<IDocument>) => {
      state.currentDocument = payload;
      state.fetchingCurrentDocumentError = null;
    },
    setDocumentFailed: (state, { payload }: PayloadAction<BillsError>) => {
      state.fetchingCurrentDocumentError = payload;
    },
    setDocumentStateSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.savingDocumentStateInProgress = payload;
    },
    setDocumentStateSavingError: (
      state,
      { payload }: PayloadAction<BillsError>
    ) => {
      state.savingDocumentStateError = payload;
    },
    setProposalStateSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.savingProposalStateInProgress = payload;
    },
    setProposalStateSuccess: (state, { payload }: PayloadAction<IProposal>) => {
      state.proposals = state.proposals.map((p) =>
        p.id === payload.id ? payload : p
      );
    },
    setProposalStateSavingError: (
      state,
      { payload }: PayloadAction<BillsError>
    ) => {
      state.savingProposalStateError = payload;
    },
    setVoteBillStateSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.savingVoteBillStateInProgress = payload;
    },
    setVoteBillStateSavingError: (
      state,
      { payload }: PayloadAction<BillsError>
    ) => {
      state.savingVoteBillStateError = payload;
    },
    setPublishingProposal: (state, { payload }: PayloadAction<boolean>) => {
      state.publishingProposalInProgress = payload;
    },
    setPublishingProposalError: (
      state,
      { payload }: PayloadAction<BillsError>
    ) => {
      state.publishingProposalError = payload;
    },
    setDeletingProposal: (state, { payload }: PayloadAction<boolean>) => {
      state.deletingProposalInProgress = payload;
    },
    setDeletingProposalError: (
      state,
      { payload }: PayloadAction<BillsError>
    ) => {
      state.deletingProposalError = payload;
    },
    setProposalsSuccess: (state, { payload }: PayloadAction<IProposal[]>) => {
      state.proposals = payload;
      state.fetchingProposalsError = null;
    },
    setProposalsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.fetchingProposalsInProgress = payload;
    },
    setBillSearchInterestsSuccess: (
      state,
      { payload }: PayloadAction<IBillSearchInterest[]>
    ) => {
      state.billSearchInterests = payload;
      state.fetchingBillSearchInterestsError = null;
    },
    setBillSearchInterestsLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.fetchingBillSearchInterestsInProgress = payload;
    },
    setBillSearchInterestsFailed: (
      state,
      { payload }: PayloadAction<BillsError>
    ) => {
      state.fetchingBillSearchInterestsError = payload;
    },
    setMyBillsSelected: (state, { payload }: PayloadAction<boolean>) => {
      state.myBillsSelected = payload;
    },
    setBillsOfInterestsSelected: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.billsOfInterestsSelected = payload;
    },
    setAllBillsSelected: (state, { payload }: PayloadAction<boolean>) => {
      state.allBillsSelected = payload;
    },
    setBillProposalsSuccess: (state, { payload }: PayloadAction<any>) => {
      state.billProposals = payload;
      state.fetchingBillProposalsError = null;
    },
    setBillProposalsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.fetchingBillProposalsInProgress = payload;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    setOffset: (state, { payload }: PayloadAction<number>) => {
      state.offset = payload;
    },
    setTag: (state, { payload }: PayloadAction<string>) => {
      state.tag = payload;
    },
    updateProposalsVotes: (state, { payload }: PayloadAction<any>) => {
      state.proposals = state.proposals.map((proposal) => {
        if (proposal.id === payload.data.proposalId) {
          if (proposal.likes.length > 0) {
            const filteredData = proposal.likes.filter((s) => {
              return s.userId === payload.data.userId;
            });
            if (filteredData.length > 0) {
              proposal.likes.map((like) => {
                if (like.userId === payload.data.userId) {
                  like.like = payload.data.like;
                }
              });
            } else {
              proposal.likes = [
                ...proposal.likes,
                {
                  id: payload.data.id,
                  like: payload.data.like,
                  createdAt: payload.data.createdAt,
                  updatedAt: payload.data.updatedAt,
                  proposalId: payload.data.proposal.id,
                  userId: payload.data.userId,
                },
              ];
            }
          } else {
            proposal.likes = [
              {
                id: payload.data.id,
                like: payload.data.like,
                createdAt: payload.data.createdAt,
                updatedAt: payload.data.updatedAt,
                proposalId: payload.data.proposal.id,
                userId: payload.data.userId,
              },
            ];
          }
        }
        return proposal;
      });
    },
  },
});

export const {
  setBillsLoading,
  setBillsSuccess,
  setBillsFailed,
  setCurrentBillLoading,
  setCurrentBillSuccess,
  setCurrentBillsFailed,
  setCurrentBillVoteCountLoading,
  setCurrentBillVoteCountSuccess,
  setCurrentBillVoteCountFailed,
  setDocumentLoading,
  setDocumentSuccess,
  setDocumentFailed,
  setDocumentStateSaving,
  updateProposalsVotes,
  setDocumentStateSavingError,
  setProposalStateSaving,
  setProposalStateSuccess,
  setProposalStateSavingError,
  setVoteBillStateSaving,
  setVoteBillStateSavingError,
  setPublishingProposal,
  setPublishingProposalError,
  setDeletingProposal,
  setDeletingProposalError,
  setProposalsSuccess,
  setProposalsLoading,
  setBillSearchInterestsLoading,
  setBillSearchInterestsSuccess,
  setBillSearchInterestsFailed,
  setMyBillsSelected,
  setBillsOfInterestsSelected,
  setAllBillsSelected,
  setBillProposalsSuccess,
  setBillProposalsLoading,
  setCurrentPage,
  setOffset,
  setTag,
} = billsSlice.actions;

type IFetchQueryOptions = {
  tag?: string;
  pageNumber?: number;
  categoryIds?: string;
  getAll?: boolean;
  sort?: string;
};

export const setTagAction =
  (tag: string): AppThunk =>
  async (dispatch) => {
    dispatch(setTag(tag));
  };

export const setCurrentPageAction =
  (currentPage: number): AppThunk =>
  async (dispatch) => {
    dispatch(setCurrentPage(currentPage));
  };

export const setOffsetAction =
  (offset: number): AppThunk =>
  async (dispatch) => {
    dispatch(setOffset(offset));
  };

export const fetchBills =
  (query?: IFetchQueryOptions): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setBillsLoading(true));
      const { data: billsResponse } = await BillsService.getAll(
        makeQueryString({ ...query, perPage: getState().bills.perPage })
      );
      dispatch(setBillsSuccess(billsResponse));
    } catch (error) {
      handleError(error);
      dispatch(setBillsFailed(error));
    } finally {
      dispatch(setBillsLoading(false));
    }
  };

export const fetchBill =
  (billId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setCurrentBillLoading(true));
      const { data: bill } = await BillsService.getOne(Number(billId));

      dispatch(setCurrentBillSuccess(bill));
    } catch (error) {
      handleError(error);
      dispatch(setCurrentBillsFailed(error));
    } finally {
      dispatch(setCurrentBillLoading(false));
    }
  };

export const fetchBillVoteCount =
  (billId: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setCurrentBillVoteCountLoading(true));
      const { data: votes } = await BillsService.getBillVoteCount(billId);

      dispatch(setCurrentBillVoteCountSuccess(votes));
    } catch (error) {
      handleError(error);
      dispatch(setCurrentBillVoteCountFailed(error));
    } finally {
      dispatch(setCurrentBillVoteCountLoading(false));
    }
  };

export const fetchDocument =
  (billId: string, documentId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentLoading(true));
      const { data: document } = await BillsService.getBillDocument(
        Number(billId),
        Number(documentId)
      );

      dispatch(setDocumentSuccess(document));
    } catch (error) {
      handleError(error);
      dispatch(setDocumentSuccess(error));
    } finally {
      dispatch(setDocumentLoading(false));
    }
  };

export const fetchProposals =
  (documentIds: number[], teamId: number): AppThunk =>
  async (dispatch) => {
    try {
      setProposalsLoading(true);
      const payload = {
        documentIds,
        teamId,
      };
      const { data: proposals } = await BillsService.getDocumentProposals(
        payload
      );
      if (proposals) {
        dispatch(setProposalsSuccess(proposals));
      }
    } catch (error) {
      handleError(error);
      dispatch(setProposalsSuccess(error));
    } finally {
      setProposalsLoading(false);
    }
  };

export const fetchBillSearchInterests = (): AppThunk => async (dispatch) => {
  try {
    setBillSearchInterestsLoading(true);
    const { data: interests } = await BillsService.getAllCategories();
    if (interests) {
      dispatch(
        setBillSearchInterestsSuccess(
          interests.map((i) => {
            return {
              id: i.id,
              name: i.name,
              selected: false,
            };
          })
        )
      );
    }
  } catch (error) {
    handleError(error);
    dispatch(setBillSearchInterestsSuccess(error));
  } finally {
    setBillSearchInterestsLoading(false);
  }
};

export const fetchBillProposals =
  (billId: number): AppThunk =>
  async (dispatch) => {
    try {
      setBillProposalsLoading(true);
      const { data: proposals } = await BillsService.getBillProposals(billId);
      if (proposals) {
        dispatch(setBillProposalsSuccess(proposals));
      }
    } catch (error) {
      handleError(error);
      dispatch(setBillProposalsSuccess(error));
    } finally {
      setBillProposalsLoading(false);
    }
  };

export const saveBillState =
  (billId: string, payload: Object): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentStateSaving(true));
      await BillsService.saveBillState(Number(billId), payload);
    } catch (error) {
      handleError(error);
      dispatch(setDocumentStateSavingError(error));
    } finally {
      dispatch(setDocumentStateSaving(false));
    }
  };

export const voteBillState =
  (billId: number, payload: Object): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setVoteBillStateSaving(true));
      await BillsService.voteBillState(billId, payload);
    } catch (error) {
      handleError(error);
      const message = error?.response?.data
        ? error.response.data.message
        : error;
      dispatch(setVoteBillStateSavingError({ message }));
    } finally {
      dispatch(setVoteBillStateSaving(false));
    }
  };

export const voteProposalState =
  (proposalId: number, value: -1 | 0 | 1): AppThunk =>
  async (dispatch) => {
    try {
      const data = await BillsService.likeProposal(proposalId, value);
      await dispatch(updateProposalsVotes(data));
    } catch (error) {
      handleError(error);
    }
  };

export const saveDocumentState =
  (billId: string, documentId: string, payload: Object): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentStateSaving(true));
      await BillsService.saveDocumentState(
        Number(billId),
        Number(documentId),
        payload
      );
    } catch (error) {
      handleError(error);
      dispatch(setDocumentStateSavingError(error));
    } finally {
      dispatch(setDocumentStateSaving(false));
    }
  };

export const saveProposalState =
  (payload: Object, proposalId?: string, isAdmin?: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setProposalStateSaving(true));
      if (proposalId) {
        const { data } = isAdmin
          ? await BillsService.adminSaveProposalState(
              Number(proposalId),
              payload
            )
          : await BillsService.saveProposalState(Number(proposalId), payload);

        dispatch(setProposalStateSuccess(data));

        return Promise.resolve(data);
      } else {
        return await BillsService.addProposalState(payload);
      }
    } catch (error) {
      handleError(error);
      const statusCode = error?.response?.data?.statusCode;
      const message = error?.response?.data
        ? error.response.data.message
        : error;
      dispatch(setProposalStateSavingError({ message, statusCode }));
    } finally {
      dispatch(setProposalStateSaving(false));
    }
  };

export const publishProposal =
  (proposalId: number, userId: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setPublishingProposal(true));
      await BillsService.publishProposal(proposalId, userId);
    } catch (error) {
      handleError(error);
      dispatch(setPublishingProposalError(error));
    } finally {
      dispatch(setPublishingProposal(false));
    }
  };

export const deleteProposal =
  (proposalId: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDeletingProposal(true));
      await BillsService.deleteProposal(proposalId);
    } catch (error) {
      handleError(error);
      dispatch(setDeletingProposalError(error));
    } finally {
      dispatch(setDeletingProposal(false));
    }
  };

export const billsSelector = (state: RootState) => state.bills;
export default billsSlice.reducer;
